import React, { useMemo } from 'react';
import { Button } from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import {
  itemWidth,
  actionsWidth
} from '~/modules/common/charts/timeline/calculations';
import {
  usePopperHandlers,
  ResourceChartTotalPopper
} from '~/modules/resourcing/components/ResourceChartTotalPopper';
import { useProjectContext } from '~/modules/resourcing/common/contexts';
import { ResourceRequestIcon } from '~/modules/common/components';
import { useMeContext } from '~/modules/me';
import { mapScaleToPeriodResolution } from '~/modules/resourcing/common/util';
import { useResourcingChartToolbarContext } from '../../../hooks';
import ResourceRequestChartFooterRow, {
  PeriodTotalsChartFooterRow
} from '../ResourceRequestChartFooterRow';
import TotalsFooterPopoverCell from '../TotalsFooterPopoverCell/TotalsFooterPopoverCell';
import { useProjectTotals2 } from './hooks/useProjectTotals2';

const leftComponentWidth = actionsWidth + itemWidth * 2;

const useStyles = makeStyles(theme => ({
  actionButton: {
    flexGrow: 0,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    transition: theme.transitions.create(['opacity'], {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.shortest
    }),
    margin: theme.spacing(0, 2, 0, 0)
  }
}));

const actionButtonIcon = <ResourceRequestIcon />;

export const ResourceRequestChartFooter = ({
  handleAddResourceRequestClick
}) => {
  const {
    project: { permittedActionUris }
  } = useProjectContext();

  const canEditProject = permittedActionUris.includes(
    'urn:replicon:project-action:edit-project'
  );
  const canViewCostData = permittedActionUris.includes(
    'urn:replicon:project-action:view-cost-data'
  );
  const classes = useStyles();
  const {
    scale,
    chartDates,
    filter,
    dateRange,
    isResourceActualModeEnabled,
    isShowColumnarTotalsEnabled
  } = useResourcingChartToolbarContext();

  const {
    featureFlags: { isPsaPrpUnisysResourceManagementHiddenEnabled }
  } = useMeContext();

  const leftComponent = useMemo(
    () =>
      canEditProject && !isPsaPrpUnisysResourceManagementHiddenEnabled ? (
        <Button
          className={classes.actionButton}
          size="small"
          color="primary"
          variant="text"
          onClick={handleAddResourceRequestClick}
          startIcon={actionButtonIcon}
        >
          <FormattedMessage id="resourceRequestButtons.requestResources" />
        </Button>
      ) : null,
    [
      canEditProject,
      classes,
      handleAddResourceRequestClick,
      isPsaPrpUnisysResourceManagementHiddenEnabled
    ]
  );

  const {
    loading,
    resourceRequestsTotals,
    resourceRequestPeriodTotals
  } = useProjectTotals2({
    filter,
    includeActualsResourceUsers: isResourceActualModeEnabled,
    dateRange: isShowColumnarTotalsEnabled ? dateRange : null,
    periodResolution: mapScaleToPeriodResolution(scale)
  });
  const {
    anchorEl,
    onMenuClose,
    onMenuClick,
    handleKeyDown
  } = usePopperHandlers();

  const showTotalComponent = Boolean(
    resourceRequestsTotals?.totalHours ||
      resourceRequestsTotals?.totalCost?.amount
  );

  const totalPopoverCell = showTotalComponent && (
    <TotalsFooterPopoverCell
      resourceRequestsTotals={resourceRequestsTotals}
      handleClick={onMenuClick}
      handleKeyDown={handleKeyDown}
      onClickAway={onMenuClose}
      canViewCostData={canViewCostData}
      loading={loading}
    >
      <ResourceChartTotalPopper
        id="resource-request-total-popper"
        anchorEl={anchorEl}
        onClose={onMenuClose}
        requestedHours={resourceRequestsTotals.totalHours}
        requestedCost={resourceRequestsTotals.totalCost}
        viewCosts={canViewCostData}
        viewAllocation={false}
        viewDifference={false}
      />
    </TotalsFooterPopoverCell>
  );

  return isShowColumnarTotalsEnabled && showTotalComponent ? (
    <PeriodTotalsChartFooterRow
      scale={scale}
      chartDates={chartDates}
      totalLabel="resourceRequestChartFooter.totalRequested"
      leftComponent={leftComponent}
      leftComponentWidth={leftComponentWidth}
      periodTotals={resourceRequestPeriodTotals}
      periodTotalsLoading={loading}
      qeIdTag="TotalRequestedPeriod"
    >
      {totalPopoverCell}
    </PeriodTotalsChartFooterRow>
  ) : (
    <ResourceRequestChartFooterRow
      scale={scale}
      scaleItemCount={chartDates.length}
      leftComponentWidth={leftComponentWidth}
      totalLabel="resourceRequestChartFooter.totalRequested"
      leftComponent={leftComponent}
    >
      {totalPopoverCell}
    </ResourceRequestChartFooterRow>
  );
};

ResourceRequestChartFooter.propTypes = {
  handleAddResourceRequestClick: PropTypes.func
};

export default ResourceRequestChartFooter;
